import React from 'react'

import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import {
  selectCurrentUser,
  selectAuthToken,
} from './userSlice'
import {
  clearFlashAlert,
  clearFlashNotice,
  setFlashAlert,
  setFlashNotice,
} from '../ui/uiSlice'
import { formatDeviseErrors } from '../../../components/devise/deviseErrors'
import { deviseFetch, showFailedRequest } from '../../../components/devise/deviseRequest'
import useSubmitting from '../../../hooks/useSubmitting'

import { LockOutlined as LockOutlinedIcon } from '@mui/icons-material'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import Grid from '@mui/material/Grid'
import Link from '@mui/material/Link'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'

const classes = {
  growBox: {
    display: 'flex',
    flexGrow: 1,
    justifyContent: 'flex-end',
  }
}

export default function Registration() {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const user = useSelector(selectCurrentUser)
  const storedAuthToken = useSelector(selectAuthToken)
  const [resending, submit] = useSubmitting()

  // Ask for the confirmation link for the pending new email to be sent again
  // (POST /user/confirmation.json with that address; the server answers 201).
  const handleResend = (e) => {
    e.preventDefault()
    dispatch(clearFlashNotice())
    dispatch(clearFlashAlert())

    const pendingEmail = user.unconfirmed_email
    const csrfToken = storedAuthToken ||
      document.querySelector('meta[name=csrf-token]')?.content || ''
    const data = new FormData()
    data.set('user[email]', pendingEmail)

    submit(async () => {
      const result = await deviseFetch(dispatch, '/user/confirmation.json', {
        method: 'POST',
        body: data,
        headers: {
          'Accept': 'application/json',
          ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
        },
        credentials: 'same-origin',
      })

      if (result.failure) {
        showFailedRequest(dispatch, result)
      } else if (result.response.status === 201) {
        dispatch(setFlashNotice(
          `We sent the link again to ${pendingEmail}. Check your inbox.`
        ))
      } else {
        dispatch(setFlashAlert(formatDeviseErrors(result.json)))
      }
    })
  }

  const handleNavClick = (e) => {
    let path = e.currentTarget.href.replace(/https?\:\/\//, '')
      .replace(e.currentTarget.host, '')
    navigate(path)
    e.preventDefault()
    e.stopPropagation()
  }

  return (
    <React.Fragment>
      <Avatar sx={{ m: 1, bgcolor: 'secondary.main' }}>
        <LockOutlinedIcon />
      </Avatar>
      <Typography component='h1' variant='h5'>
        Your Account details
      </Typography>
      <Box sx={{ mt: 2, width: '100%', maxWidth: 480 }}>
        {/* A light grey panel with a border, so it stands out on the page */}
        <Paper
          variant="outlined"
          sx={{ p: 2, mb: 2, bgcolor: 'grey.100', borderColor: 'grey.400' }}
        >
          <Box
            component="dl"
            sx={{
              m: 0,
              display: 'grid',
              gridTemplateColumns: 'max-content 1fr',
              columnGap: 3,
              rowGap: 1,
              // The labels are smaller than the values: line up their baselines
              alignItems: 'baseline',
            }}
          >
            <Typography
              component="dt"
              variant="body2"
              color="text.secondary"
              sx={{ fontWeight: 'bold' }}
            >
              Login
            </Typography>
            <Typography component="dd" variant="body1" sx={{ m: 0 }}>
              {user?.login}
            </Typography>
            <Typography
              component="dt"
              variant="body2"
              color="text.secondary"
              sx={{ fontWeight: 'bold' }}
            >
              Email
            </Typography>
            <Typography component="dd" variant="body1" sx={{ m: 0 }}>
              {user?.email}
            </Typography>
          </Box>
        </Paper>
        {user?.unconfirmed_email && (
          <Alert
            severity="info"
            variant="outlined"
            sx={{ mb: 2, fontSize: '0.8125rem' }}
          >
            <AlertTitle sx={{ fontSize: '0.875rem' }}>
              Change of email waiting for confirmation
            </AlertTitle>
            You requested to change your email to{' '}
            <em>{user.unconfirmed_email}</em>. You still need to confirm that
            address. Check your inbox and click the link we sent to finish the
            change. Can't find it?{' '}
            <Link
              component="button"
              type="button"
              variant="inherit"
              onClick={handleResend}
              disabled={resending}
              sx={{ verticalAlign: 'baseline' }}
            >
              Resend the link
            </Link>
            {resending && (
              <CircularProgress
                size={12}
                sx={{ ml: 0.5, verticalAlign: 'middle' }}
              />
            )}.
          </Alert>
        )}
        <Grid container>
          <Grid sx={{ flexGrow: 1 }}>
            <Link
              onClick={handleNavClick}
              href="/app/account/edit_email"
              variant="body2"
              >
              {"Edit email"}
            </Link>
          </Grid>
          <Grid>
            <Link 
              onClick={handleNavClick}
              href="/app/account/edit_password"
              variant="body2"
              >
              {"Change password"}
            </Link>
          </Grid>
        </Grid>
        <Box sx={{ mt: 3 }}>
          <Link
            onClick={handleNavClick}
            href="/app/account/delete"
            variant="body2"
            color="error"
            >
            {"Delete my account"}
          </Link>
        </Box>           
      </Box>
    </React.Fragment>
  )
}
